import { useState, type FormEvent } from 'react';
import type { Alias, AliasInput } from '../../identity/api';
import { Button, Chip, Select, TextField } from '@nyce/admin-kit/ui';
import { ConfirmButton, EmptyState, ErrorBox, TableScroller } from '../../components/common';

export type AliasLevel = 'customer' | 'tenant';

/** Systems AUTH documents per level (§10.8); anything else is typed. */
export const ALIAS_SYSTEMS: Record<AliasLevel, { system: string; hint: string; placeholder: string }[]> = {
  customer: [
    { system: 'pms', hint: 'PMS / datacollector customer id', placeholder: '1546' },
    { system: 'vat', hint: 'VAT registration number', placeholder: 'SE556677889901' },
  ],
  tenant: [
    {
      system: 'workserver',
      hint: 'WorkServer name as sent in datacollector payloads',
      placeholder: 'STARK-WS1',
    },
    { system: 'printqueue', hint: 'Print queue TenantId (as the WorkServer sends it)', placeholder: 'STARK' },
    { system: 'automation-gw', hint: 'Automation gateway tenant GUID', placeholder: '6f1c…' },
    { system: 'scim', hint: 'SCIM gateway endpoint tenantId', placeholder: 'stark' },
    {
      system: 'gke',
      hint: '<clusterId>:<namespace> as in the hub registry',
      placeholder: 'prod-eu:nyce-stark',
    },
  ],
};

const SYSTEM_RX = /^[a-z][a-z0-9-]{1,31}$/;
const sameKey = (a: AliasInput, b: AliasInput) => a.system === b.system && a.value === b.value;
const toInput = ({ system, value }: AliasInput): AliasInput => ({ system, value });

/** Compact `system · value` chips for list rows. */
export function AliasChips({ aliases }: { aliases?: Alias[] }) {
  if (!aliases?.length) return <span className="nyk-ui-hint">—</span>;
  return (
    <span className="nyk-chips">
      {aliases.map((a) => (
        <Chip key={`${a.system}:${a.value}`} size="sm">
          {a.system} · {a.value}
        </Chip>
      ))}
    </span>
  );
}

/**
 * Ids other systems (PMS, WorkServer, the automation gateway, SCIM, GKE) use for this customer or tenant. Every change
 * replaces the whole set (`PUT …/aliases`): removal works for any value AUTH accepts, and a pair another customer or
 * tenant owns comes back as 409 with the owner named.
 */
export function AliasesEditor({
  level,
  aliases,
  canWrite,
  replace,
  notify,
}: {
  level: AliasLevel;
  aliases: Alias[];
  canWrite: boolean;
  replace: (next: AliasInput[]) => Promise<void>;
  notify: { success: (t: string) => void; error: (t: string) => void };
}) {
  const known = ALIAS_SYSTEMS[level];
  const [system, setSystem] = useState(known[0].system);
  const [customSystem, setCustomSystem] = useState('');
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const effectiveSystem = (system === 'custom' ? customSystem : system).trim().toLowerCase();
  const systemOk = SYSTEM_RX.test(effectiveSystem);
  const preset = known.find((k) => k.system === system);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    const next = { system: effectiveSystem, value: value.trim() };
    if (!systemOk || !next.value) return;
    if (aliases.some((a) => sameKey(a, next))) {
      setError(`${next.system} · ${next.value} is already an alias here.`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await replace([...aliases.map(toInput), next]);
      setValue('');
      notify.success(`Added alias ${next.system} · ${next.value}`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const remove = async (a: Alias) => {
    setError(null);
    try {
      await replace(aliases.filter((x) => !sameKey(x, a)).map(toInput));
      notify.success(`Removed alias ${a.system} · ${a.value}`);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <>
      <ErrorBox error={error} />
      <TableScroller>
        <table className="nyk-ui-table nyk-cards" data-testid="aliases-table">
          <thead>
            <tr>
              <th>System</th>
              <th>Value</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {aliases.map((a) => (
              <tr key={`${a.system}:${a.value}`} data-alias={`${a.system}:${a.value}`}>
                <td data-label="System">
                  <Chip size="sm" selected={known.some((k) => k.system === a.system)}>
                    {a.system}
                  </Chip>
                </td>
                <td data-label="Value" className="nyk-card-title nyk-wrap">
                  <code>{a.value}</code>
                </td>
                <td className="nyk-row-actions">
                  {canWrite && (
                    <ConfirmButton
                      size="sm"
                      variant="danger"
                      message={`Remove alias ${a.system} · ${a.value}? Systems that look it up no longer find this ${level}.`}
                      onConfirm={() => void remove(a)}
                    >
                      Remove
                    </ConfirmButton>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroller>
      {!aliases.length && (
        <EmptyState
          title="No aliases."
          hint={`Other systems' ids for this ${level} (${known.map((k) => k.system).join(', ')}) connect them to AUTH.`}
        />
      )}
      {canWrite && (
        <form onSubmit={add} data-testid="alias-form" style={{ marginTop: 16 }}>
          <div className="nyk-inline-form">
            <Select
              label="System"
              value={system}
              onChange={(v) => setSystem(v)}
              options={[
                ...known.map((k) => ({ value: k.system, label: k.system })),
                { value: 'custom', label: 'other…' },
              ]}
            />
            {system === 'custom' && (
              <TextField
                label="Custom system"
                value={customSystem}
                onChange={(e) => setCustomSystem(e.target.value)}
                placeholder="lowercase-slug"
                maxLength={32}
                required
              />
            )}
            <TextField
              label="Value"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={preset?.placeholder}
              maxLength={200}
              required
            />
            <Button type="submit" loading={busy} disabled={!systemOk || !value.trim()}>
              Add
            </Button>
          </div>
          <p className="nyk-card-intro" style={{ marginTop: 12, marginBottom: 0 }}>
            {preset
              ? `${preset.hint}.`
              : 'System: a lowercase slug (letters, digits, -). Value: up to 200 characters, no /.'}{' '}
            A pair names one customer or tenant across AUTH.
          </p>
        </form>
      )}
    </>
  );
}
